/**
 * Hex battle terrain config (Option 1: individual hex terrain).
 * Uses online sample image URLs for terrain; fallback to TERRAIN_COLORS if load fails.
 */

export const TERRAIN_GRASS = 0
export const TERRAIN_RIVER = 1
export const TERRAIN_MOUNTAIN = 2
export const TERRAIN_CASTLE = 3
export const TERRAIN_FOREST = 4

export type TerrainType = 0 | 1 | 2 | 3 | 4

/** Temple center positions (passable). */
export const TEMPLE_CENTER_1 = { col: 11, row: 28 }
export const TEMPLE_CENTER_2 = { col: 12, row: 4 }

/** Map3 portal: stepping on the entry hex teleports the character to the exit hex. */
export const MAP3_PORTALS: Record<string, { from: { col: number; row: number }; to: { col: number; row: number } }> = {
  'map3': { from: { col: 13, row: 0 }, to: { col: 1, row: 36 } },
}

/** Map3 watchtower footprint — same as {@link TERRAIN_MOUNTAIN}. Sync with MVM_TOWER_POSITIONS map3. */
export const MAP3_TOWER_FOOTPRINT_HEXES: readonly { col: number; row: number }[] = [
  { col: 30, row: 6 },
  { col: 29, row: 6 },
  { col: 29, row: 5 },
  { col: 29, row: 4 },
  { col: 28, row: 3 },
  { col: 14, row: 2 },
  { col: 15, row: 2 },
  { col: 14, row: 1 },
  { col: 13, row: 1 },
  { col: 4, row: 4 },
  { col: 5, row: 4 },
  { col: 4, row: 3 },
  { col: 4, row: 2 },
  { col: 3, row: 1 },
  { col: 27, row: 35 },
  { col: 28, row: 35 },
  { col: 27, row: 34 },
  { col: 28, row: 34 },
  { col: 29, row: 34 },
  { col: 27, row: 33 },
  { col: 28, row: 33 },
  { col: 17, row: 35 },
  { col: 18, row: 35 },
  { col: 17, row: 34 },
  { col: 18, row: 34 },
  { col: 19, row: 34 },
  { col: 17, row: 33 },
  { col: 18, row: 33 },
  { col: 9, row: 34 },
  { col: 10, row: 34 },
  { col: 8, row: 33 },
  { col: 9, row: 33 },
  { col: 10, row: 33 },
  { col: 9, row: 32 },
  { col: 10, row: 32 },
]

/** Map1 watchtower footprint — impassable like {@link TERRAIN_MOUNTAIN}. Sync with MVM_TOWER_POSITIONS map1. */
export const MAP1_TOWER_FOOTPRINT_HEXES: readonly { col: number; row: number }[] = [
  { col: 28, row: 3 },
  { col: 27, row: 3 },
  { col: 28, row: 2 },
  { col: 27, row: 1 },
  { col: 27, row: 0 },
  { col: 16, row: 4 },
  { col: 17, row: 4 },
  { col: 16, row: 3 },
  { col: 15, row: 3 },
  { col: 15, row: 1 },
  { col: 10, row: 3 },
  { col: 9, row: 3 },
  { col: 10, row: 2 },
  { col: 9, row: 1 },
  { col: 9, row: 0 },
  { col: 7, row: 31 },
  { col: 8, row: 31 },
  { col: 7, row: 32 },
  { col: 8, row: 32 },
  { col: 9, row: 32 },
  { col: 7, row: 33 },
  { col: 8, row: 33 },
  { col: 17, row: 34 },
  { col: 18, row: 34 },
  { col: 16, row: 33 },
  { col: 17, row: 33 },
  { col: 18, row: 33 },
  { col: 17, row: 32 },
  { col: 18, row: 32 },
  { col: 24, row: 34 },
  { col: 25, row: 34 },
  { col: 23, row: 33 },
  { col: 24, row: 33 },
  { col: 25, row: 33 },
  { col: 24, row: 32 },
  { col: 25, row: 32 },
]

/** Map2 watchtower footprint — impassable like mountains. Sync with MVM_TOWER_POSITIONS map2. */
export const MAP2_TOWER_FOOTPRINT_HEXES: readonly { col: number; row: number }[] = [
  { col: 22, row: 3 },
  { col: 23, row: 3 },
  { col: 23, row: 2 },
  { col: 22, row: 1 },
  { col: 22, row: 0 },
  { col: 16, row: 3 },
  { col: 17, row: 3 },
  { col: 17, row: 2 },
  { col: 16, row: 1 },
  { col: 16, row: 0 },
  { col: 10, row: 3 },
  { col: 11, row: 3 },
  { col: 11, row: 2 },
  { col: 10, row: 1 },
  { col: 10, row: 0 },
  { col: 29, row: 35 },
  { col: 30, row: 35 },
  { col: 29, row: 34 },
  { col: 30, row: 34 },
  { col: 31, row: 34 },
  { col: 29, row: 33 },
  { col: 30, row: 33 },
  { col: 21, row: 35 },
  { col: 22, row: 35 },
  { col: 21, row: 34 },
  { col: 22, row: 34 },
  { col: 23, row: 34 },
  { col: 21, row: 33 },
  { col: 22, row: 33 },
  { col: 15, row: 35 },
  { col: 16, row: 35 },
  { col: 15, row: 34 },
  { col: 16, row: 34 },
  { col: 17, row: 34 },
  { col: 15, row: 33 },
  { col: 16, row: 33 },
]

const TOWER_HEX_POSITIONS: Record<string, { col: number; row: number }[]> = {
  map1: [...MAP1_TOWER_FOOTPRINT_HEXES],
  map2: [...MAP2_TOWER_FOOTPRINT_HEXES],
  map3: [...MAP3_TOWER_FOOTPRINT_HEXES],
}

export function getTowerHexPositions(mapType: string): { col: number; row: number }[] {
  return TOWER_HEX_POSITIONS[mapType] ?? []
}

export function isPassable(terrain: TerrainType): boolean {
  return terrain === TERRAIN_GRASS || terrain === TERRAIN_FOREST
}

/** Movement cost per tile (for pathfinding). Rivers and mountains are impassable. */
export function getMovementCost(terrain: TerrainType): number {
  if (terrain === TERRAIN_GRASS) return 1
  if (terrain === TERRAIN_FOREST) return 1
  return Infinity // Rivers, mountains, and castles are impassable
}

/** Fill colors for terrain (Graphics only; no image loading). */
export const TERRAIN_COLORS: Record<TerrainType, number> = {
  [TERRAIN_GRASS]: 0x3a6a3a,
  [TERRAIN_RIVER]: 0x2a5080,
  [TERRAIN_MOUNTAIN]: 0x505050,
  [TERRAIN_CASTLE]: 0x8a7a6a,
  [TERRAIN_FOREST]: 0x2d5a2d,
}

/** Hexes that are always unavailable to move on and have no hex image (map1 and map2). */
export const BLOCKED_HEX_COORDINATES: readonly { col: number; row: number }[] = [
  { col: 0, row: 0 }, { col: 1, row: 0 }, { col: 2, row: 0 },
  { col: 0, row: 1 }, { col: 1, row: 1 },
  { col: 0, row: 2 }, { col: 1, row: 2 },
  { col: 0, row: 3 }, { col: 0, row: 4 },
  { col: 33, row: 0 }, { col: 33, row: 1 }, { col: 34, row: 0 }, { col: 34, row: 1 }, { col: 34, row: 2 }, { col: 34, row: 3 },
  { col: 0, row: 36 }, { col: 0, row: 37 }, { col: 0, row: 38 }, { col: 0, row: 39 },
  { col: 1, row: 38 }, { col: 1, row: 39 },
  { col: 32, row: 39 }, { col: 33, row: 39 }, { col: 34, row: 39 },
  { col: 33, row: 38 }, { col: 34, row: 38 },
  { col: 33, row: 37 }, { col: 34, row: 37 },
  { col: 34, row: 36 }, { col: 34, row: 35 },
]

const BLOCKED_HEX_KEYS = new Set(BLOCKED_HEX_COORDINATES.map(({ col, row }) => `${col},${row}`))

export function isBlockedHex(col: number, row: number): boolean {
  return BLOCKED_HEX_KEYS.has(`${col},${row}`)
}

/** Structure = 3–4 hexes grouped for mountain/castle/forest. */
export interface TerrainStructure {
  type: 'mountain' | 'castle' | 'forest'
  cells: { col: number; row: number }[]
}

export type MapType = 'map1' | 'map2' | 'map3'

/** Normalize `battle.mapType` from the server for hex terrain + Pixi background. */
export function normalizeServerMapType(mapType: string | undefined | null): MapType {
  if (mapType === 'map2') return 'map2'
  if (mapType === 'map3') return 'map3'
  return 'map1'
}

/** Build a 2D terrain map: [col][row] = TerrainType. */
export function buildSampleTerrainMap(width: number, height: number, mapType: MapType = 'map1'): TerrainType[][] {
  if (mapType === 'map3') return buildMap3Terrain(width, height)
  if (mapType === 'map1') return buildMap1Terrain(width, height)
  return buildMap2Terrain(width, height)
}

/** Build terrain for Map 1 (Forest map) */
function buildMap1Terrain(width: number, height: number): TerrainType[][] {
  const map: TerrainType[][] = []
  for (let col = 0; col < width; col++) {
    map[col] = []
    for (let row = 0; row < height; row++) {
      map[col][row] = TERRAIN_GRASS
    }
  }

  const setRange = (terrain: TerrainType, colStart: number, colEnd: number, row: number) => {
    for (let col = colStart; col <= colEnd; col++) {
      if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = terrain
    }
  }
  const setCell = (terrain: TerrainType, col: number, row: number) => {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = terrain
  }

  // Mountains
  // Row 2 cols 22-25 and row 3 cols 22-25 are normal ground (passable)
  setRange(TERRAIN_MOUNTAIN, 22, 25, 4)
  setRange(TERRAIN_MOUNTAIN, 22, 25, 4)
  setRange(TERRAIN_MOUNTAIN, 21, 25, 5)
  setRange(TERRAIN_MOUNTAIN, 20, 25, 6)
  setRange(TERRAIN_MOUNTAIN, 18, 24, 7)
  setRange(TERRAIN_MOUNTAIN, 18, 23, 8)
  setRange(TERRAIN_MOUNTAIN, 18, 22, 9)
  setRange(TERRAIN_MOUNTAIN, 33, 34, 14)
  setRange(TERRAIN_MOUNTAIN, 32, 34, 15)
  setRange(TERRAIN_MOUNTAIN, 32, 34, 16)
  setRange(TERRAIN_MOUNTAIN, 31, 34, 17)
  setRange(TERRAIN_MOUNTAIN, 32, 34, 18)
  setRange(TERRAIN_MOUNTAIN, 33, 34, 19)
  setRange(TERRAIN_MOUNTAIN, 22, 26, 17)
  setRange(TERRAIN_MOUNTAIN, 22, 26, 18)
  setRange(TERRAIN_MOUNTAIN, 21, 25, 19)
  setRange(TERRAIN_MOUNTAIN, 21, 29, 20)
  setRange(TERRAIN_MOUNTAIN, 21, 23, 21)
  setRange(TERRAIN_MOUNTAIN, 20, 22, 23)
  setRange(TERRAIN_MOUNTAIN, 20, 22, 24)
  setRange(TERRAIN_MOUNTAIN, 20, 22, 25)
  setCell(TERRAIN_MOUNTAIN, 0, 24)
  setCell(TERRAIN_MOUNTAIN, 0, 25)
  setRange(TERRAIN_MOUNTAIN, 0, 1, 26)
  setCell(TERRAIN_MOUNTAIN, 0, 27)
  setCell(TERRAIN_MOUNTAIN, 0, 28)
  setRange(TERRAIN_MOUNTAIN, 0, 3, 8)
  setRange(TERRAIN_MOUNTAIN, 0, 2, 9)
  setRange(TERRAIN_MOUNTAIN, 0, 2, 10)
  setRange(TERRAIN_MOUNTAIN, 0, 1, 11)
  setRange(TERRAIN_MOUNTAIN, 0, 1, 12)
  setCell(TERRAIN_MOUNTAIN, 0, 13)
  setCell(TERRAIN_MOUNTAIN, 0, 14)

  // Lake (TERRAIN_RIVER)
  setRange(TERRAIN_RIVER, 26, 28, 21)
  setRange(TERRAIN_RIVER, 25, 29, 22)
  setRange(TERRAIN_RIVER, 24, 28, 23)
  setRange(TERRAIN_RIVER, 24, 27, 24)
  setRange(TERRAIN_RIVER, 24, 27, 25)
  setRange(TERRAIN_RIVER, 24, 25, 26)
  setRange(TERRAIN_RIVER, 10, 12, 29)
  for (let row = 29; row <= 30; row++) setRange(TERRAIN_RIVER, 11, 15, row)
  setCell(TERRAIN_RIVER, 15, 29)
  setCell(TERRAIN_RIVER, 11, 31)
  setRange(TERRAIN_RIVER, 13, 14, 31)
  setCell(TERRAIN_RIVER, 9, 27)
  setCell(TERRAIN_RIVER, 20, 26)

  // Normal passable area (TERRAIN_GRASS) — rows 20–28, col 11–15; row 21 col 11–25
  setRange(TERRAIN_GRASS, 11, 15, 20)
  setRange(TERRAIN_GRASS, 11, 25, 21)
  setRange(TERRAIN_GRASS, 11, 15, 22)
  setRange(TERRAIN_GRASS, 11, 15, 23)
  setRange(TERRAIN_GRASS, 11, 15, 24)
  setRange(TERRAIN_GRASS, 11, 15, 25)
  setRange(TERRAIN_GRASS, 11, 15, 26)
  setRange(TERRAIN_GRASS, 11, 15, 27)
  setRange(TERRAIN_GRASS, 11, 15, 28)

  // Forests
  setCell(TERRAIN_FOREST, 8, 10)
  setRange(TERRAIN_FOREST, 7, 8, 11)
  setRange(TERRAIN_FOREST, 4, 9, 12)
  setRange(TERRAIN_FOREST, 4, 8, 13)
  setRange(TERRAIN_FOREST, 4, 8, 14)
  setRange(TERRAIN_FOREST, 4, 7, 15)
  setRange(TERRAIN_FOREST, 4, 7, 16)
  setRange(TERRAIN_FOREST, 3, 7, 17)
  setRange(TERRAIN_FOREST, 4, 7, 18)
  setRange(TERRAIN_FOREST, 3, 6, 19)
  setRange(TERRAIN_FOREST, 3, 6, 20)
  setRange(TERRAIN_FOREST, 2, 4, 21)
  setRange(TERRAIN_FOREST, 2, 6, 22)
  setRange(TERRAIN_FOREST, 1, 5, 23)
  setCell(TERRAIN_FOREST, 6, 24)
  setRange(TERRAIN_FOREST, 25, 27, 15)
  setRange(TERRAIN_FOREST, 25, 26, 16)
  setCell(TERRAIN_FOREST, 19, 19)
  setCell(TERRAIN_FOREST, 20, 19)
  setRange(TERRAIN_FOREST, 19, 20, 20)
  setRange(TERRAIN_FOREST, 17, 19, 21)
  setRange(TERRAIN_FOREST, 21, 23, 22)
  setCell(TERRAIN_FOREST, 19, 23)
  setCell(TERRAIN_FOREST, 19, 24)
  setRange(TERRAIN_FOREST, 19, 22, 25)
  setRange(TERRAIN_FOREST, 20, 22, 26)
  setRange(TERRAIN_FOREST, 20, 21, 27)
  setRange(TERRAIN_FOREST, 11, 13, 32)
  setRange(TERRAIN_FOREST, 10, 14, 33)
  setRange(TERRAIN_FOREST, 10, 14, 34)
  setRange(TERRAIN_FOREST, 10, 11, 35)

  // Cliffs (TERRAIN_MOUNTAIN)
  const cliffCells = [
    { col: 7, row: 19 }, { col: 7, row: 20 }, { col: 5, row: 21 }, { col: 6, row: 21 }, { col: 7, row: 21 },
    { col: 7, row: 22 }, { col: 6, row: 23 },
    { col: 11, row: 18 }, { col: 12, row: 18 }, { col: 13, row: 18 }, { col: 14, row: 18 },
    { col: 14, row: 16 }, { col: 14, row: 17 }, { col: 15, row: 16 }, { col: 14, row: 15 }, { col: 14, row: 14 },
    { col: 15, row: 14 }, { col: 14, row: 13 }, { col: 15, row: 12 },
    { col: 13, row: 5 }, { col: 14, row: 6 }, { col: 15, row: 6 }, { col: 14, row: 5 }, { col: 15, row: 5 },
    { col: 16, row: 6 }, { col: 17, row: 6 }, { col: 16, row: 5 }, { col: 17, row: 5 },
    { col: 14, row: 3 }, { col: 15, row: 3 },
  ]
  cliffCells.forEach(({ col, row }) => setCell(TERRAIN_MOUNTAIN, col, row))

  // Ensure temple centers are passable (TERRAIN_GRASS)
  if (colIsInBounds(TEMPLE_CENTER_1.col, width) && rowIsInBounds(TEMPLE_CENTER_1.row, height)) {
    map[TEMPLE_CENTER_1.col][TEMPLE_CENTER_1.row] = TERRAIN_GRASS
  }
  if (colIsInBounds(TEMPLE_CENTER_2.col, width) && rowIsInBounds(TEMPLE_CENTER_2.row, height)) {
    map[TEMPLE_CENTER_2.col][TEMPLE_CENTER_2.row] = TERRAIN_GRASS
  }

  // Blocked hexes: unavailable to move, no hex image (map1)
  BLOCKED_HEX_COORDINATES.forEach(({ col, row }) => {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_MOUNTAIN
  })

  for (const { col, row } of MAP1_TOWER_FOOTPRINT_HEXES) {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_MOUNTAIN
  }

  return map
}

/** Build terrain for Map 2 (River map) */
function buildMap2Terrain(width: number, height: number): TerrainType[][] {
  const map: TerrainType[][] = []
  for (let col = 0; col < width; col++) {
    map[col] = []
    for (let row = 0; row < height; row++) {
      map[col][row] = TERRAIN_GRASS
    }
  }

  // Mountains
  const setMountainRange = (colStart: number, colEnd: number, row: number) => {
    for (let col = colStart; col <= colEnd; col++) {
      if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_MOUNTAIN
    }
  }
  setMountainRange(24, 28, 5)
  setMountainRange(24, 29, 6)
  setMountainRange(22, 28, 7)
  setMountainRange(22, 27, 8)
  setMountainRange(22, 27, 9)
  setMountainRange(23, 27, 10)
  setMountainRange(22, 25, 11)
  setMountainRange(22, 25, 12)
  setMountainRange(33, 34, 14)
  setMountainRange(33, 34, 15)
  setMountainRange(32, 34, 16)
  setMountainRange(31, 34, 17)
  setMountainRange(31, 34, 18)
  setMountainRange(30, 34, 19)
  setMountainRange(30, 34, 20)
  setMountainRange(30, 34, 21)
  setMountainRange(32, 34, 22)
  setMountainRange(31, 34, 23)
  setMountainRange(6, 11, 34)
  setMountainRange(5, 10, 35)
  setMountainRange(3, 11, 36)
  setMountainRange(2, 10, 37)
  setMountainRange(2, 11, 38)
  setMountainRange(1, 9, 39)
  setMountainRange(0, 2, 15)
  setMountainRange(0, 2, 16)
  setMountainRange(0, 2, 17)
  setMountainRange(0, 1, 18)
  if (colIsInBounds(0, width) && rowIsInBounds(19, height)) map[0][19] = TERRAIN_MOUNTAIN
  if (colIsInBounds(0, width) && rowIsInBounds(20, height)) map[0][20] = TERRAIN_MOUNTAIN

  // Rivers
  const setRiverRange = (colStart: number, colEnd: number, row: number) => {
    for (let col = colStart; col <= colEnd; col++) {
      if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_RIVER
    }
  }
  const riverCells: { col: number; row: number }[] = [
    { col: 24, row: 15 }, { col: 25, row: 16 }, { col: 26, row: 17 }, { col: 26, row: 18 }, { col: 11, row: 28 }, { col: 25, row: 19 }, { col: 25, row: 20 },
    { col: 25, row: 22 }, { col: 24, row: 23 }, { col: 24, row: 24 }, { col: 23, row: 25 }, { col: 23, row: 26 },
    { col: 22, row: 28 }, { col: 21, row: 31 }, { col: 21, row: 32 }, { col: 20, row: 33 }, { col: 20, row: 34 },
    { col: 19, row: 35 }, { col: 13, row: 10 },
  ]
  riverCells.forEach(({ col, row }) => {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_RIVER
  })
  setRiverRange(23, 24, 13)
  setRiverRange(24, 25, 14)
  setRiverRange(24, 25, 21)
  setRiverRange(21, 22, 29)
  setRiverRange(21, 22, 30)
  setRiverRange(19, 20, 36)
  setRiverRange(18, 20, 37)
  setRiverRange(18, 20, 38)
  setRiverRange(17, 21, 39)
  setRiverRange(9, 11, 27)
  setRiverRange(8, 11, 28)
  setRiverRange(7, 10, 29)
  setRiverRange(7, 9, 9)
  setRiverRange(7, 9, 10)
  setRiverRange(6, 13, 11)
  setRiverRange(6, 10, 12)
  setRiverRange(5, 11, 13)
  setRiverRange(5, 11, 14)
  setRiverRange(4, 9, 15)
  setRiverRange(4, 9, 16)
  setRiverRange(4, 7, 17)
  setRiverRange(5, 7, 18)

  // Forests
  const setForestRange = (colStart: number, colEnd: number, row: number) => {
    for (let col = colStart; col <= colEnd; col++) {
      if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_FOREST
    }
  }
  const forestCells: { col: number; row: number }[] = [
    { col: 0, row: 21 }, { col: 0, row: 25 }, { col: 0, row: 27 }, { col: 0, row: 28 }, { col: 0, row: 29 },
    { col: 12, row: 38 },
  ]
  forestCells.forEach(({ col, row }) => {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_FOREST
  })
  setForestRange(0, 2, 22)
  setForestRange(0, 2, 23)
  setForestRange(0, 1, 24)
  setForestRange(0, 1, 26)
  setForestRange(10, 12, 33)
  setForestRange(12, 14, 34)
  setForestRange(11, 13, 35)
  setForestRange(11, 14, 36)
  setForestRange(11, 13, 37)
  setForestRange(10, 13, 39)

  // Ensure temple centers are passable (TERRAIN_GRASS) — except (11,28) which stays river/impassable
  if (colIsInBounds(TEMPLE_CENTER_2.col, width) && rowIsInBounds(TEMPLE_CENTER_2.row, height)) {
    map[TEMPLE_CENTER_2.col][TEMPLE_CENTER_2.row] = TERRAIN_GRASS
  }

  // Blocked hexes: unavailable to move, no hex image (map2)
  BLOCKED_HEX_COORDINATES.forEach(({ col, row }) => {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_MOUNTAIN
  })

  for (const { col, row } of MAP2_TOWER_FOOTPRINT_HEXES) {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_MOUNTAIN
  }

  return map
}

function colIsInBounds(col: number, width: number): boolean {
  return col >= 0 && col < width
}
function rowIsInBounds(row: number, height: number): boolean {
  return row >= 0 && row < height
}

/** Structures for drawing grouped mountain/castle visuals. */
export function getSampleStructures(width: number, height: number, mapType: MapType = 'map1'): TerrainStructure[] {
  if (mapType === 'map3') return getMap3Structures(width, height)
  if (mapType === 'map1') return getMap1Structures(width, height)
  return getMap2Structures(width, height)
}

/** Structures for Map 1 */
function getMap1Structures(width: number, height: number): TerrainStructure[] {
  // Build first mountain grid cells
  const firstMountainCells: { col: number; row: number }[] = []
  // Row 2: col 21-23
  for (let col = 21; col <= 23; col++) {
    if (col >= 0 && col < width && 2 >= 0 && 2 < height) firstMountainCells.push({ col, row: 2 })
  }
  // Row 3: col 19-22
  for (let col = 19; col <= 22; col++) {
    if (col >= 0 && col < width && 3 >= 0 && 3 < height) firstMountainCells.push({ col, row: 3 })
  }
  // Row 4: col 19-23
  for (let col = 19; col <= 23; col++) {
    if (col >= 0 && col < width && 4 >= 0 && 4 < height) firstMountainCells.push({ col, row: 4 })
  }
  // Row 5: col 18-22
  for (let col = 18; col <= 22; col++) {
    if (col >= 0 && col < width && 5 >= 0 && 5 < height) firstMountainCells.push({ col, row: 5 })
  }
  // Row 6: col 18-22
  for (let col = 18; col <= 22; col++) {
    if (col >= 0 && col < width && 6 >= 0 && 6 < height) firstMountainCells.push({ col, row: 6 })
  }
  // Row 7: col 16-21
  for (let col = 16; col <= 21; col++) {
    if (col >= 0 && col < width && 7 >= 0 && 7 < height) firstMountainCells.push({ col, row: 7 })
  }
  // Row 8: col 17-21
  for (let col = 17; col <= 21; col++) {
    if (col >= 0 && col < width && 8 >= 0 && 8 < height) firstMountainCells.push({ col, row: 8 })
  }

  // Build second mountain grid cells
  const secondMountainCells: { col: number; row: number }[] = []
  // Row 16: col 23-26, and col 25
  for (let col = 23; col <= 26; col++) {
    if (col >= 0 && col < width && 16 >= 0 && 16 < height) secondMountainCells.push({ col, row: 16 })
  }
  if (25 >= 0 && 25 < width && 15 >= 0 && 15 < height) secondMountainCells.push({ col: 25, row: 15 })
  // Row 17: col 22-25
  for (let col = 22; col <= 25; col++) {
    if (col >= 0 && col < width && 17 >= 0 && 17 < height) secondMountainCells.push({ col, row: 17 })
  }
  // Row 18: col 22-29
  for (let col = 22; col <= 29; col++) {
    if (col >= 0 && col < width && 18 >= 0 && 18 < height) secondMountainCells.push({ col, row: 18 })
  }
  // Row 19: col 21-24
  for (let col = 21; col <= 24; col++) {
    if (col >= 0 && col < width && 19 >= 0 && 19 < height) secondMountainCells.push({ col, row: 19 })
  }
  // Row 20: col 21-24
  for (let col = 21; col <= 24; col++) {
    if (col >= 0 && col < width && 20 >= 0 && 20 < height) secondMountainCells.push({ col, row: 20 })
  }
  // Row 22: col 22-23
  for (let col = 22; col <= 23; col++) {
    if (col >= 0 && col < width && 22 >= 0 && 22 < height) secondMountainCells.push({ col, row: 22 })
  }
  // Row 23: col 21-22
  for (let col = 21; col <= 22; col++) {
    if (col >= 0 && col < width && 23 >= 0 && 23 < height) secondMountainCells.push({ col, row: 23 })
  }
  // Row 24: col 21-22
  for (let col = 21; col <= 22; col++) {
    if (col >= 0 && col < width && 24 >= 0 && 24 < height) secondMountainCells.push({ col, row: 24 })
  }

  // Build lake grid cells
  const lakeCells: { col: number; row: number }[] = []
  // Row 19: col 26-28
  for (let col = 26; col <= 28; col++) {
    if (col >= 0 && col < width && 19 >= 0 && 19 < height) lakeCells.push({ col, row: 19 })
  }
  // Row 20: col 25-26
  for (let col = 25; col <= 26; col++) {
    if (col >= 0 && col < width && 20 >= 0 && 20 < height) lakeCells.push({ col, row: 20 })
  }
  // Row 21: col 24-25, 27-28
  for (let col = 24; col <= 25; col++) {
    if (col >= 0 && col < width && 21 >= 0 && 21 < height) lakeCells.push({ col, row: 21 })
  }
  for (let col = 27; col <= 28; col++) {
    if (col >= 0 && col < width && 21 >= 0 && 21 < height) lakeCells.push({ col, row: 21 })
  }
  // Row 22: col 25-28
  for (let col = 25; col <= 28; col++) {
    if (col >= 0 && col < width && 22 >= 0 && 22 < height) lakeCells.push({ col, row: 22 })
  }
  // Row 23: col 25-27
  for (let col = 25; col <= 27; col++) {
    if (col >= 0 && col < width && 23 >= 0 && 23 < height) lakeCells.push({ col, row: 23 })
  }
  // Row 24: col 25-26
  for (let col = 25; col <= 26; col++) {
    if (col >= 0 && col < width && 24 >= 0 && 24 < height) lakeCells.push({ col, row: 24 })
  }
  // Disable cells: 28,20 and 29,20
  if (28 >= 0 && 28 < width && 20 >= 0 && 20 < height) lakeCells.push({ col: 28, row: 20 })
  if (29 >= 0 && 29 < width && 20 >= 0 && 20 < height) lakeCells.push({ col: 29, row: 20 })

  // Build additional impassable cells
  const additionalCells: { col: number; row: number }[] = []
  // Row 31: col 11-16
  for (let col = 11; col <= 16; col++) {
    if (col >= 0 && col < width && 31 >= 0 && 31 < height) additionalCells.push({ col, row: 31 })
  }
  // Row 30: col 13-14, 16-17
  for (let col = 13; col <= 14; col++) {
    if (col >= 0 && col < width && 30 >= 0 && 30 < height) additionalCells.push({ col, row: 30 })
  }
  for (let col = 16; col <= 17; col++) {
    if (col >= 0 && col < width && 30 >= 0 && 30 < height) additionalCells.push({ col, row: 30 })
  }
  // Row 32: col 14-16
  for (let col = 14; col <= 16; col++) {
    if (col >= 0 && col < width && 32 >= 0 && 32 < height) additionalCells.push({ col, row: 32 })
  }
  // Additional mountain cells
  // Row 11: col 33-34
  for (let col = 33; col <= 34; col++) {
    if (col >= 0 && col < width && 11 >= 0 && 11 < height) additionalCells.push({ col, row: 11 })
  }
  // Row 12: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 12 >= 0 && 12 < height) additionalCells.push({ col, row: 12 })
  }
  // Row 13: col 24-26, 32 (excluding 27-31 to make them passable)
  for (let col = 24; col <= 26; col++) {
    if (col >= 0 && col < width && 13 >= 0 && 13 < height) additionalCells.push({ col, row: 13 })
  }
  if (32 >= 0 && 32 < width && 13 >= 0 && 13 < height) additionalCells.push({ col: 32, row: 13 })
  // Row 14: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 14 >= 0 && 14 < height) additionalCells.push({ col, row: 14 })
  }
  // Row 15: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 15 >= 0 && 15 < height) additionalCells.push({ col, row: 15 })
  }
  // Row 16: col 33-34
  for (let col = 33; col <= 34; col++) {
    if (col >= 0 && col < width && 16 >= 0 && 16 < height) additionalCells.push({ col, row: 16 })
  }
  // Row 28: col 0-1
  for (let col = 0; col <= 1; col++) {
    if (col >= 0 && col < width && 28 >= 0 && 28 < height) additionalCells.push({ col, row: 28 })
  }
  // Row 29: col 0
  if (0 >= 0 && 0 < width && 29 >= 0 && 29 < height) additionalCells.push({ col: 0, row: 29 })
  // Row 30: col 0-1
  for (let col = 0; col <= 1; col++) {
    if (col >= 0 && col < width && 30 >= 0 && 30 < height) additionalCells.push({ col, row: 30 })
  }
  // Row 31: col 0
  if (0 >= 0 && 0 < width && 31 >= 0 && 31 < height) additionalCells.push({ col: 0, row: 31 })
  // Row 32: col 0
  if (0 >= 0 && 0 < width && 32 >= 0 && 32 < height) additionalCells.push({ col: 0, row: 32 })
  // Row 34: col 0
  if (0 >= 0 && 0 < width && 34 >= 0 && 34 < height) additionalCells.push({ col: 0, row: 34 })
  // Row 19: col 11-12
  for (let col = 11; col <= 12; col++) {
    if (col >= 0 && col < width && 19 >= 0 && 19 < height) additionalCells.push({ col, row: 19 })
  }
  // Row 18: col 13, 14
  if (13 >= 0 && 13 < width && 18 >= 0 && 18 < height) additionalCells.push({ col: 13, row: 18 })
  if (14 >= 0 && 14 < width && 18 >= 0 && 18 < height) additionalCells.push({ col: 14, row: 18 })
  // Row 17: col 13
  if (13 >= 0 && 13 < width && 17 >= 0 && 17 < height) additionalCells.push({ col: 13, row: 17 })
  // Row 16: col 14
  if (14 >= 0 && 14 < width && 16 >= 0 && 16 < height) additionalCells.push({ col: 14, row: 16 })
  // Row 15: col 13
  if (13 >= 0 && 13 < width && 15 >= 0 && 15 < height) additionalCells.push({ col: 13, row: 15 })
  // Row 14: col 13-14
  for (let col = 13; col <= 14; col++) {
    if (col >= 0 && col < width && 14 >= 0 && 14 < height) additionalCells.push({ col, row: 14 })
  }
  // Row 4: col 15
  if (15 >= 0 && 15 < width && 4 >= 0 && 4 < height) additionalCells.push({ col: 15, row: 4 })
  // Row 5: col 14
  if (14 >= 0 && 14 < width && 5 >= 0 && 5 < height) additionalCells.push({ col: 14, row: 5 })
  // Row 6: col 11-14
  for (let col = 11; col <= 14; col++) {
    if (col >= 0 && col < width && 6 >= 0 && 6 < height) additionalCells.push({ col, row: 6 })
  }

  // Build forest cells
  const forestCells: { col: number; row: number }[] = []
  // Row 13: col 25-26
  for (let col = 25; col <= 26; col++) {
    if (col >= 0 && col < width && 13 >= 0 && 13 < height) forestCells.push({ col, row: 13 })
  }
  // Row 14: col 25-26
  for (let col = 25; col <= 26; col++) {
    if (col >= 0 && col < width && 14 >= 0 && 14 < height) forestCells.push({ col, row: 14 })
  }
  // Row 15: col 24
  if (24 >= 0 && 24 < width && 15 >= 0 && 15 < height) forestCells.push({ col: 24, row: 15 })
  // Row 18: col 21
  if (21 >= 0 && 21 < width && 18 >= 0 && 18 < height) forestCells.push({ col: 21, row: 18 })
  // Row 19: col 19-20
  for (let col = 19; col <= 20; col++) {
    if (col >= 0 && col < width && 19 >= 0 && 19 < height) forestCells.push({ col, row: 19 })
  }
  // Row 20: col 19-20
  for (let col = 19; col <= 20; col++) {
    if (col >= 0 && col < width && 20 >= 0 && 20 < height) forestCells.push({ col, row: 20 })
  }
  // Row 21: col 18-19, 21-23
  for (let col = 18; col <= 19; col++) {
    if (col >= 0 && col < width && 21 >= 0 && 21 < height) forestCells.push({ col, row: 21 })
  }
  for (let col = 21; col <= 23; col++) {
    if (col >= 0 && col < width && 21 >= 0 && 21 < height) forestCells.push({ col, row: 21 })
  }
  // Row 23: col 20
  if (20 >= 0 && 20 < width && 23 >= 0 && 23 < height) forestCells.push({ col: 20, row: 23 })
  // Row 24: col 20, 23
  if (20 >= 0 && 20 < width && 24 >= 0 && 24 < height) forestCells.push({ col: 20, row: 24 })
  if (23 >= 0 && 23 < width && 24 >= 0 && 24 < height) forestCells.push({ col: 23, row: 24 })
  // Row 23: col 23
  if (23 >= 0 && 23 < width && 23 >= 0 && 23 < height) forestCells.push({ col: 23, row: 23 })
  // Row 25: col 20-23
  for (let col = 20; col <= 23; col++) {
    if (col >= 0 && col < width && 25 >= 0 && 25 < height) forestCells.push({ col, row: 25 })
  }
  // Row 26: col 22-23
  for (let col = 22; col <= 23; col++) {
    if (col >= 0 && col < width && 26 >= 0 && 26 < height) forestCells.push({ col, row: 26 })
  }
  // Row 28: col 9-10
  for (let col = 9; col <= 10; col++) {
    if (col >= 0 && col < width && 28 >= 0 && 28 < height) forestCells.push({ col, row: 28 })
  }
  // Row 29: col 8-9
  for (let col = 8; col <= 9; col++) {
    if (col >= 0 && col < width && 29 >= 0 && 29 < height) forestCells.push({ col, row: 29 })
  }
  // Row 30: col 8-11
  for (let col = 8; col <= 11; col++) {
    if (col >= 0 && col < width && 30 >= 0 && 30 < height) forestCells.push({ col, row: 30 })
  }
  // Row 31: col 8-10
  for (let col = 8; col <= 10; col++) {
    if (col >= 0 && col < width && 31 >= 0 && 31 < height) forestCells.push({ col, row: 31 })
  }
  // Row 32: col 10
  if (10 >= 0 && 10 < width && 32 >= 0 && 32 < height) forestCells.push({ col: 10, row: 32 })
  // Row 26: col 2-7
  for (let col = 2; col <= 7; col++) {
    if (col >= 0 && col < width && 26 >= 0 && 26 < height) forestCells.push({ col, row: 26 })
  }
  // Row 25: col 2-6
  for (let col = 2; col <= 6; col++) {
    if (col >= 0 && col < width && 25 >= 0 && 25 < height) forestCells.push({ col, row: 25 })
  }
  // Row 24: col 2-6 (but NOT 7,24)
  for (let col = 2; col <= 6; col++) {
    if (col >= 0 && col < width && 24 >= 0 && 24 < height) forestCells.push({ col, row: 24 })
  }
  // Row 23: col 2-4 (but NOT 5,23-7,23)
  for (let col = 2; col <= 4; col++) {
    if (col >= 0 && col < width && 23 >= 0 && 23 < height) forestCells.push({ col, row: 23 })
  }
  // Row 22: col 3-6 (but NOT 7,22)
  for (let col = 3; col <= 6; col++) {
    if (col >= 0 && col < width && 22 >= 0 && 22 < height) forestCells.push({ col, row: 22 })
  }
  // Row 21: col 3-6 (but NOT 7,21)
  for (let col = 3; col <= 6; col++) {
    if (col >= 0 && col < width && 21 >= 0 && 21 < height) forestCells.push({ col, row: 21 })
  }
  // Row 20: col 4-7
  for (let col = 4; col <= 7; col++) {
    if (col >= 0 && col < width && 20 >= 0 && 20 < height) forestCells.push({ col, row: 20 })
  }
  // Row 19: col 2-6
  for (let col = 2; col <= 6; col++) {
    if (col >= 0 && col < width && 19 >= 0 && 19 < height) forestCells.push({ col, row: 19 })
  }
  // Row 18: col 3-7
  for (let col = 3; col <= 7; col++) {
    if (col >= 0 && col < width && 18 >= 0 && 18 < height) forestCells.push({ col, row: 18 })
  }
  // Row 17: col 3-6
  for (let col = 3; col <= 6; col++) {
    if (col >= 0 && col < width && 17 >= 0 && 17 < height) forestCells.push({ col, row: 17 })
  }
  // Row 16: col 3-7
  for (let col = 3; col <= 7; col++) {
    if (col >= 0 && col < width && 16 >= 0 && 16 < height) forestCells.push({ col, row: 16 })
  }
  // Row 15: col 3-7
  for (let col = 3; col <= 7; col++) {
    if (col >= 0 && col < width && 15 >= 0 && 15 < height) forestCells.push({ col, row: 15 })
  }
  // Row 14: col 4-8
  for (let col = 4; col <= 8; col++) {
    if (col >= 0 && col < width && 14 >= 0 && 14 < height) forestCells.push({ col, row: 14 })
  }
  // Row 13: col 5-7
  for (let col = 5; col <= 7; col++) {
    if (col >= 0 && col < width && 13 >= 0 && 13 < height) forestCells.push({ col, row: 13 })
  }

  return [
    {
      type: 'mountain',
      cells: [
        { col: 8, row: 3 },
        { col: 9, row: 3 },
        { col: 8, row: 4 },
        { col: 9, row: 4 },
      ].filter((c) => c.col >= 0 && c.col < width && c.row >= 0 && c.row < height),
    },
    {
      type: 'mountain',
      cells: firstMountainCells,
    },
    {
      type: 'mountain',
      cells: secondMountainCells,
    },
    {
      type: 'mountain',
      cells: lakeCells,
    },
    {
      type: 'mountain',
      cells: additionalCells,
    },
    {
      type: 'castle',
      cells: [
        { col: 14, row: 5 },
        { col: 15, row: 5 },
        { col: 14, row: 6 },
        { col: 15, row: 6 },
      ].filter((c) => c.col >= 0 && c.col < width && c.row >= 0 && c.row < height),
    },
    {
      type: 'forest',
      cells: forestCells,
    },
  ]
}

/** Structures for Map 2 */
function getMap2Structures(width: number, height: number): TerrainStructure[] {
  // Build mountain cells based on new map2 terrain
  const mountainCells: { col: number; row: number }[] = []
  // Row 2: col 26-27
  for (let col = 26; col <= 27; col++) {
    if (col >= 0 && col < width && 2 >= 0 && 2 < height) mountainCells.push({ col, row: 2 })
  }
  // Row 3: col 22-26
  for (let col = 22; col <= 26; col++) {
    if (col >= 0 && col < width && 3 >= 0 && 3 < height) mountainCells.push({ col, row: 3 })
  }
  // Row 4: col 22-27
  for (let col = 22; col <= 27; col++) {
    if (col >= 0 && col < width && 4 >= 0 && 4 < height) mountainCells.push({ col, row: 4 })
  }
  // Row 5: col 21-25
  for (let col = 21; col <= 25; col++) {
    if (col >= 0 && col < width && 5 >= 0 && 5 < height) mountainCells.push({ col, row: 5 })
  }
  // Row 6: col 21-25
  for (let col = 21; col <= 25; col++) {
    if (col >= 0 && col < width && 6 >= 0 && 6 < height) mountainCells.push({ col, row: 6 })
  }
  // Row 7: col 20-25
  for (let col = 20; col <= 25; col++) {
    if (col >= 0 && col < width && 7 >= 0 && 7 < height) mountainCells.push({ col, row: 7 })
  }
  // Row 8: col 22-25
  for (let col = 22; col <= 25; col++) {
    if (col >= 0 && col < width && 8 >= 0 && 8 < height) mountainCells.push({ col, row: 8 })
  }
  // Row 9: col 22-24
  for (let col = 22; col <= 24; col++) {
    if (col >= 0 && col < width && 9 >= 0 && 9 < height) mountainCells.push({ col, row: 9 })
  }
  // Row 10: col 21-24
  for (let col = 21; col <= 24; col++) {
    if (col >= 0 && col < width && 10 >= 0 && 10 < height) mountainCells.push({ col, row: 10 })
  }
  // Row 11: col 21, 33-34
  if (21 >= 0 && 21 < width && 11 >= 0 && 11 < height) mountainCells.push({ col: 21, row: 11 })
  for (let col = 33; col <= 34; col++) {
    if (col >= 0 && col < width && 11 >= 0 && 11 < height) mountainCells.push({ col, row: 11 })
  }
  // Row 12: col 33-34
  for (let col = 33; col <= 34; col++) {
    if (col >= 0 && col < width && 12 >= 0 && 12 < height) mountainCells.push({ col, row: 12 })
  }
  // Row 13: col 33-34
  for (let col = 33; col <= 34; col++) {
    if (col >= 0 && col < width && 13 >= 0 && 13 < height) mountainCells.push({ col, row: 13 })
  }
  // Row 14: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 14 >= 0 && 14 < height) mountainCells.push({ col, row: 14 })
  }
  // Row 15: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 15 >= 0 && 15 < height) mountainCells.push({ col, row: 15 })
  }
  // Row 16: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 16 >= 0 && 16 < height) mountainCells.push({ col, row: 16 })
  }
  // Row 17: col 31-34
  for (let col = 31; col <= 34; col++) {
    if (col >= 0 && col < width && 17 >= 0 && 17 < height) mountainCells.push({ col, row: 17 })
  }
  // Row 18: col 0-1, 34
  for (let col = 0; col <= 1; col++) {
    if (col >= 0 && col < width && 18 >= 0 && 18 < height) mountainCells.push({ col, row: 18 })
  }
  if (34 >= 0 && 34 < width && 18 >= 0 && 18 < height) mountainCells.push({ col: 34, row: 18 })
  // Row 19: col 0
  if (0 >= 0 && 0 < width && 19 >= 0 && 19 < height) mountainCells.push({ col: 0, row: 19 })
  // Row 20: col 0
  if (0 >= 0 && 0 < width && 20 >= 0 && 20 < height) mountainCells.push({ col: 0, row: 20 })
  // Row 37: col 9-12
  for (let col = 9; col <= 12; col++) {
    if (col >= 0 && col < width && 37 >= 0 && 37 < height) mountainCells.push({ col, row: 37 })
  }
  // Row 38: col 8-13
  for (let col = 8; col <= 13; col++) {
    if (col >= 0 && col < width && 38 >= 0 && 38 < height) mountainCells.push({ col, row: 38 })
  }
  // Row 39: col 7-13
  for (let col = 7; col <= 13; col++) {
    if (col >= 0 && col < width && 39 >= 0 && 39 < height) mountainCells.push({ col, row: 39 })
  }

  // Build forest cells
  const forestCells: { col: number; row: number }[] = []
  // Single forest cells
  const singleForestCells = [
    { col: 0, row: 24 },
    { col: 0, row: 26 },
    { col: 1, row: 26 },
    { col: 0, row: 27 },
    { col: 0, row: 28 },
    { col: 1, row: 28 },
    { col: 0, row: 29 },
    { col: 0, row: 30 },
    { col: 1, row: 3 },
    { col: 0, row: 31 },
    { col: 0, row: 32 },
    { col: 1, row: 32 },
    { col: 0, row: 33 },
    { col: 0, row: 34 },
    { col: 31, row: 19 },
    { col: 33, row: 19 },
    { col: 34, row: 19 },
    { col: 33, row: 20 },
    { col: 34, row: 20 },
    { col: 33, row: 17 },
    { col: 32, row: 17 },
    { col: 34, row: 17 },
  ]
  singleForestCells.forEach(({ col, row }) => {
    if (col >= 0 && col < width && row >= 0 && row < height) forestCells.push({ col, row })
  })
  // Row 25: col 0-1
  for (let col = 0; col <= 1; col++) {
    if (col >= 0 && col < width && 25 >= 0 && 25 < height) forestCells.push({ col, row: 25 })
  }
  // Row 18: col 32-34
  for (let col = 32; col <= 34; col++) {
    if (col >= 0 && col < width && 18 >= 0 && 18 < height) forestCells.push({ col, row: 18 })
  }
  // Row 35: col 13-16
  for (let col = 13; col <= 16; col++) {
    if (col >= 0 && col < width && 35 >= 0 && 35 < height) forestCells.push({ col, row: 35 })
  }
  // Row 36: col 12-16
  for (let col = 12; col <= 16; col++) {
    if (col >= 0 && col < width && 36 >= 0 && 36 < height) forestCells.push({ col, row: 36 })
  }
  // Row 37: col 13-15
  for (let col = 13; col <= 15; col++) {
    if (col >= 0 && col < width && 37 >= 0 && 37 < height) forestCells.push({ col, row: 37 })
  }
  // Row 38: col 13-16
  for (let col = 13; col <= 16; col++) {
    if (col >= 0 && col < width && 38 >= 0 && 38 < height) forestCells.push({ col, row: 38 })
  }
  // Row 39: col 13-16
  for (let col = 13; col <= 16; col++) {
    if (col >= 0 && col < width && 39 >= 0 && 39 < height) forestCells.push({ col, row: 39 })
  }

  return [
    {
      type: 'mountain',
      cells: mountainCells,
    },
    {
      type: 'castle',
      cells: [
        { col: 14, row: 5 },
        { col: 15, row: 5 },
        { col: 14, row: 6 },
        { col: 15, row: 6 },
      ].filter((c) => c.col >= 0 && c.col < width && c.row >= 0 && c.row < height),
    },
    {
      type: 'forest',
      cells: forestCells,
    },
  ]
}

// ---------------------------------------------------------------------------
// Map 3 terrain
// ---------------------------------------------------------------------------

function buildMap3Terrain(width: number, height: number): TerrainType[][] {
  const map: TerrainType[][] = []
  for (let col = 0; col < width; col++) {
    map[col] = []
    for (let row = 0; row < height; row++) {
      map[col][row] = TERRAIN_GRASS
    }
  }

  const set = (t: TerrainType, col: number, row: number) => {
    if (col >= 0 && col < width && row >= 0 && row < height) map[col][row] = t
  }
  const setRange = (t: TerrainType, colStart: number, colEnd: number, row: number) => {
    for (let c = colStart; c <= colEnd; c++) set(t, c, row)
  }

  // --- Rivers ---
  set(TERRAIN_RIVER, 6, 2); set(TERRAIN_RIVER, 7, 2)
  set(TERRAIN_RIVER, 6, 3); set(TERRAIN_RIVER, 6, 4)
  set(TERRAIN_RIVER, 5, 5); set(TERRAIN_RIVER, 6, 5); set(TERRAIN_RIVER, 7, 4); set(TERRAIN_RIVER, 8, 4)
  set(TERRAIN_RIVER, 9, 4); set(TERRAIN_RIVER, 10, 4); set(TERRAIN_RIVER, 11, 4)
  set(TERRAIN_RIVER, 7, 5); set(TERRAIN_RIVER, 8, 5); set(TERRAIN_RIVER, 9, 5)
  set(TERRAIN_RIVER, 13, 4); set(TERRAIN_RIVER, 12, 3); set(TERRAIN_RIVER, 13, 3)
  set(TERRAIN_RIVER, 14, 3); set(TERRAIN_RIVER, 15, 3)
  set(TERRAIN_RIVER, 14, 4); set(TERRAIN_RIVER, 15, 4); set(TERRAIN_RIVER, 16, 4); set(TERRAIN_RIVER, 15, 5)
  set(TERRAIN_RIVER, 18, 4); set(TERRAIN_RIVER, 17, 4); set(TERRAIN_RIVER, 17, 3)
  set(TERRAIN_RIVER, 18, 2); set(TERRAIN_RIVER, 18, 3); set(TERRAIN_RIVER, 19, 3); set(TERRAIN_RIVER, 20, 4)
  set(TERRAIN_RIVER, 20, 3); set(TERRAIN_RIVER, 20, 2); set(TERRAIN_RIVER, 19, 2); set(TERRAIN_RIVER, 19, 1)
  set(TERRAIN_RIVER, 21, 2); set(TERRAIN_RIVER, 20, 1); set(TERRAIN_RIVER, 20, 0)
  set(TERRAIN_RIVER, 33, 3); set(TERRAIN_RIVER, 33, 2); set(TERRAIN_RIVER, 32, 3)
  set(TERRAIN_RIVER, 33, 4); set(TERRAIN_RIVER, 32, 5)
  set(TERRAIN_RIVER, 6, 6); set(TERRAIN_RIVER, 7, 6)
  set(TERRAIN_RIVER, 6, 7); set(TERRAIN_RIVER, 6, 8); set(TERRAIN_RIVER, 7, 8); set(TERRAIN_RIVER, 8, 8)
  set(TERRAIN_RIVER, 5, 9); set(TERRAIN_RIVER, 6, 9)
  set(TERRAIN_RIVER, 0, 18); set(TERRAIN_RIVER, 1, 18); set(TERRAIN_RIVER, 2, 18)
  set(TERRAIN_RIVER, 0, 19); set(TERRAIN_RIVER, 1, 19)
  set(TERRAIN_RIVER, 0, 20); set(TERRAIN_RIVER, 1, 20)
  set(TERRAIN_RIVER, 0, 21); set(TERRAIN_RIVER, 1, 21)
  set(TERRAIN_RIVER, 0, 22); set(TERRAIN_RIVER, 1, 22)
  set(TERRAIN_RIVER, 0, 23); set(TERRAIN_RIVER, 1, 23)
  set(TERRAIN_RIVER, 0, 24); set(TERRAIN_RIVER, 1, 24)
  set(TERRAIN_RIVER, 0, 25)
  set(TERRAIN_RIVER, 0, 26); set(TERRAIN_RIVER, 1, 26)
  set(TERRAIN_RIVER, 0, 27); set(TERRAIN_RIVER, 0, 28)
  set(TERRAIN_RIVER, 1, 31); set(TERRAIN_RIVER, 2, 31)
  set(TERRAIN_RIVER, 2, 21)
  set(TERRAIN_RIVER, 0, 32); set(TERRAIN_RIVER, 1, 32); set(TERRAIN_RIVER, 2, 32); set(TERRAIN_RIVER, 3, 32)
  set(TERRAIN_RIVER, 0, 33); set(TERRAIN_RIVER, 1, 33); set(TERRAIN_RIVER, 2, 33); set(TERRAIN_RIVER, 3, 33)
  set(TERRAIN_RIVER, 3, 31)
  set(TERRAIN_RIVER, 1, 37); set(TERRAIN_RIVER, 2, 37)
  set(TERRAIN_RIVER, 3, 38); set(TERRAIN_RIVER, 4, 38)
  set(TERRAIN_RIVER, 3, 39); set(TERRAIN_RIVER, 4, 39)
  set(TERRAIN_RIVER, 22, 34); set(TERRAIN_RIVER, 23, 34); set(TERRAIN_RIVER, 25, 34); set(TERRAIN_RIVER, 25, 33)
  set(TERRAIN_RIVER, 21, 35); set(TERRAIN_RIVER, 22, 35); set(TERRAIN_RIVER, 23, 35)
  set(TERRAIN_RIVER, 21, 36); set(TERRAIN_RIVER, 22, 36); set(TERRAIN_RIVER, 23, 36)
  set(TERRAIN_RIVER, 21, 37)
  set(TERRAIN_RIVER, 32, 25); set(TERRAIN_RIVER, 33, 25); set(TERRAIN_RIVER, 34, 25)
  set(TERRAIN_RIVER, 32, 26); set(TERRAIN_RIVER, 33, 26); set(TERRAIN_RIVER, 34, 26)
  set(TERRAIN_RIVER, 32, 27); set(TERRAIN_RIVER, 33, 27)
  set(TERRAIN_RIVER, 33, 24); set(TERRAIN_RIVER, 34, 24); set(TERRAIN_RIVER, 31, 25)

  // --- Mountains ---
  setRange(TERRAIN_MOUNTAIN, 9, 12, 0)
  setRange(TERRAIN_MOUNTAIN, 24, 27, 0)
  setRange(TERRAIN_MOUNTAIN, 23, 28, 1)
  setRange(TERRAIN_MOUNTAIN, 24, 29, 2)
  setRange(TERRAIN_MOUNTAIN, 23, 28, 3)
  setRange(TERRAIN_MOUNTAIN, 24, 29, 4)
  setRange(TERRAIN_MOUNTAIN, 23, 28, 5)
  set(TERRAIN_MOUNTAIN, 33, 4)
  set(TERRAIN_MOUNTAIN, 20, 10); set(TERRAIN_MOUNTAIN, 23, 10)
  set(TERRAIN_MOUNTAIN, 20, 12); set(TERRAIN_MOUNTAIN, 21, 12); set(TERRAIN_MOUNTAIN, 22, 12); set(TERRAIN_MOUNTAIN, 23, 12)
  set(TERRAIN_MOUNTAIN, 23, 11); set(TERRAIN_MOUNTAIN, 22, 11)
  set(TERRAIN_MOUNTAIN, 20, 13); set(TERRAIN_MOUNTAIN, 21, 13)
  set(TERRAIN_MOUNTAIN, 34, 6)
  setRange(TERRAIN_MOUNTAIN, 32, 34, 7)
  setRange(TERRAIN_MOUNTAIN, 32, 34, 8)
  setRange(TERRAIN_MOUNTAIN, 31, 34, 9)
  setRange(TERRAIN_MOUNTAIN, 32, 34, 10)
  set(TERRAIN_MOUNTAIN, 32, 35); set(TERRAIN_MOUNTAIN, 33, 35)
  set(TERRAIN_MOUNTAIN, 32, 36); set(TERRAIN_MOUNTAIN, 33, 36)
  set(TERRAIN_MOUNTAIN, 31, 37); set(TERRAIN_MOUNTAIN, 32, 37)
  set(TERRAIN_MOUNTAIN, 32, 38); set(TERRAIN_MOUNTAIN, 33, 38); set(TERRAIN_MOUNTAIN, 31, 39)
  set(TERRAIN_MOUNTAIN, 24, 31); set(TERRAIN_MOUNTAIN, 24, 36); set(TERRAIN_MOUNTAIN, 24, 37)
  set(TERRAIN_MOUNTAIN, 6, 37)
  set(TERRAIN_MOUNTAIN, 12, 31); set(TERRAIN_MOUNTAIN, 13, 31); set(TERRAIN_MOUNTAIN, 14, 31)
  set(TERRAIN_MOUNTAIN, 13, 32); set(TERRAIN_MOUNTAIN, 14, 32)
  set(TERRAIN_MOUNTAIN, 13, 33)
  set(TERRAIN_MOUNTAIN, 16, 31)
  set(TERRAIN_MOUNTAIN, 13, 29); set(TERRAIN_MOUNTAIN, 14, 29)
  set(TERRAIN_MOUNTAIN, 14, 30); set(TERRAIN_MOUNTAIN, 15, 30)
  set(TERRAIN_MOUNTAIN, 3, 28)
  set(TERRAIN_MOUNTAIN, 5, 17); set(TERRAIN_MOUNTAIN, 6, 17)
  setRange(TERRAIN_MOUNTAIN, 5, 7, 18)
  set(TERRAIN_MOUNTAIN, 15, 19)
  setRange(TERRAIN_MOUNTAIN, 4, 8, 19)
  set(TERRAIN_MOUNTAIN, 16, 20); set(TERRAIN_MOUNTAIN, 17, 20)
  setRange(TERRAIN_MOUNTAIN, 5, 9, 20)
  setRange(TERRAIN_MOUNTAIN, 5, 8, 21)
  setRange(TERRAIN_MOUNTAIN, 5, 9, 22)
  set(TERRAIN_MOUNTAIN, 8, 24)
  set(TERRAIN_MOUNTAIN, 10, 23); set(TERRAIN_MOUNTAIN, 11, 23); set(TERRAIN_MOUNTAIN, 11, 24)
  set(TERRAIN_MOUNTAIN, 13, 24); set(TERRAIN_MOUNTAIN, 14, 24); set(TERRAIN_MOUNTAIN, 13, 25)
  set(TERRAIN_MOUNTAIN, 15, 25); set(TERRAIN_MOUNTAIN, 14, 25)
  set(TERRAIN_MOUNTAIN, 14, 23); set(TERRAIN_MOUNTAIN, 14, 22); set(TERRAIN_MOUNTAIN, 13, 22); set(TERRAIN_MOUNTAIN, 13, 23)
  set(TERRAIN_MOUNTAIN, 14, 21); set(TERRAIN_MOUNTAIN, 15, 21); set(TERRAIN_MOUNTAIN, 16, 21); set(TERRAIN_MOUNTAIN, 17, 21)
  set(TERRAIN_MOUNTAIN, 15, 22); set(TERRAIN_MOUNTAIN, 16, 22); set(TERRAIN_MOUNTAIN, 17, 22)
  set(TERRAIN_MOUNTAIN, 15, 23); set(TERRAIN_MOUNTAIN, 16, 23)
  setRange(TERRAIN_MOUNTAIN, 16, 18, 24)
  set(TERRAIN_MOUNTAIN, 19, 21); set(TERRAIN_MOUNTAIN, 19, 20)
  set(TERRAIN_MOUNTAIN, 21, 27); set(TERRAIN_MOUNTAIN, 22, 26); set(TERRAIN_MOUNTAIN, 21, 26)

  // --- Rivers (bottom-left) ---
  set(TERRAIN_RIVER, 3, 37); set(TERRAIN_RIVER, 4, 37); set(TERRAIN_RIVER, 5, 37)
  set(TERRAIN_RIVER, 5, 38)

  // --- Mountains (bottom-left) ---
  set(TERRAIN_MOUNTAIN, 4, 34); set(TERRAIN_MOUNTAIN, 5, 34); set(TERRAIN_MOUNTAIN, 6, 34)
  set(TERRAIN_MOUNTAIN, 3, 35); set(TERRAIN_MOUNTAIN, 4, 35); set(TERRAIN_MOUNTAIN, 5, 35)
  set(TERRAIN_MOUNTAIN, 3, 36); set(TERRAIN_MOUNTAIN, 4, 36); set(TERRAIN_MOUNTAIN, 5, 36); set(TERRAIN_MOUNTAIN, 6, 36)

  // 3,33 is passable grass (override previous river)
  set(TERRAIN_GRASS, 3, 33)

  // --- Forests ---
  set(TERRAIN_FOREST, 7, 17); set(TERRAIN_FOREST, 8, 17); set(TERRAIN_FOREST, 9, 17)
  setRange(TERRAIN_FOREST, 8, 11, 18)
  setRange(TERRAIN_FOREST, 9, 11, 19)
  setRange(TERRAIN_FOREST, 2, 4, 20)
  set(TERRAIN_FOREST, 10, 24); set(TERRAIN_FOREST, 12, 24)
  set(TERRAIN_FOREST, 16, 18); set(TERRAIN_FOREST, 16, 19); set(TERRAIN_FOREST, 17, 19)
  setRange(TERRAIN_FOREST, 20, 22, 24)
  setRange(TERRAIN_FOREST, 20, 22, 25)
  set(TERRAIN_FOREST, 12, 29); set(TERRAIN_FOREST, 12, 30)
  set(TERRAIN_FOREST, 11, 31); set(TERRAIN_FOREST, 11, 32); set(TERRAIN_FOREST, 12, 32)
  set(TERRAIN_FOREST, 11, 33); set(TERRAIN_FOREST, 12, 33)
  set(TERRAIN_FOREST, 15, 32); set(TERRAIN_FOREST, 16, 32)
  set(TERRAIN_FOREST, 15, 31); set(TERRAIN_FOREST, 16, 30); set(TERRAIN_FOREST, 17, 30)
  set(TERRAIN_FOREST, 32, 32); set(TERRAIN_FOREST, 33, 32); set(TERRAIN_FOREST, 34, 32)
  setRange(TERRAIN_FOREST, 32, 34, 33)
  setRange(TERRAIN_FOREST, 31, 34, 34)
  set(TERRAIN_FOREST, 30, 35); set(TERRAIN_FOREST, 31, 35)
  set(TERRAIN_FOREST, 31, 36)
  setRange(TERRAIN_FOREST, 31, 34, 20)
  setRange(TERRAIN_FOREST, 30, 34, 21)
  setRange(TERRAIN_FOREST, 30, 34, 22)
  setRange(TERRAIN_FOREST, 30, 34, 23)
  setRange(TERRAIN_FOREST, 32, 34, 24)

  // --- Mountains (override forests) ---
  setRange(TERRAIN_MOUNTAIN, 5, 13, 20)
  setRange(TERRAIN_MOUNTAIN, 9, 13, 21)
  setRange(TERRAIN_MOUNTAIN, 10, 12, 22)
  set(TERRAIN_MOUNTAIN, 12, 23)

  // Blocked hexes
  BLOCKED_HEX_COORDINATES.forEach(({ col, row }) => {
    if (colIsInBounds(col, width) && rowIsInBounds(row, height)) map[col][row] = TERRAIN_MOUNTAIN
  })

  // Watchtower footprints (map3): impassable like mountains; applied last so river/forest never override.
  for (const { col, row } of MAP3_TOWER_FOOTPRINT_HEXES) {
    set(TERRAIN_MOUNTAIN, col, row)
  }

  return map
}

function getMap3Structures(width: number, height: number): TerrainStructure[] {
  const inB = (col: number, row: number) => col >= 0 && col < width && row >= 0 && row < height
  const cells = (coords: [number, number][]) => coords.filter(([c, r]) => inB(c, r)).map(([col, row]) => ({ col, row }))
  const range = (cs: number, ce: number, row: number): [number, number][] => {
    const out: [number, number][] = []
    for (let c = cs; c <= ce; c++) out.push([c, row])
    return out
  }

  const mountainCells = cells([
    ...range(9, 12, 0),
    ...range(24, 27, 0), ...range(23, 28, 1), ...range(24, 29, 2), ...range(23, 28, 3),
    ...range(24, 29, 4), ...range(23, 28, 5),
    [33, 4], [20, 10], [23, 10],
    [20, 12], [21, 12], [22, 12], [23, 12], [23, 11], [22, 11],
    [20, 13], [21, 13],
    [34, 6], ...range(32, 34, 7), ...range(32, 34, 8), ...range(31, 34, 9), ...range(32, 34, 10),
    [32, 35], [33, 35], [32, 36], [33, 36], [31, 37], [32, 37], [32, 38], [33, 38], [31, 39],
    [24, 31], [24, 36], [24, 37], [6, 37],
    [12, 31], [13, 31], [14, 31], [13, 32], [14, 32], [13, 33], [16, 31],
    [13, 29], [14, 29], [14, 30], [15, 30], [3, 28],
    [5, 17], [6, 17], ...range(5, 7, 18), [15, 19], ...range(4, 8, 19),
    [16, 20], [17, 20], ...range(5, 13, 20),
    ...range(5, 8, 21), ...range(9, 13, 21), ...range(5, 9, 22), ...range(10, 12, 22),
    [12, 23], [8, 24], [10, 23], [11, 23], [11, 24],
    [13, 24], [14, 24], [13, 25], [15, 25], [14, 25],
    [14, 23], [14, 22], [13, 22], [13, 23],
    [14, 21], [15, 21], [16, 21], [17, 21],
    [15, 22], [16, 22], [17, 22], [15, 23], [16, 23],
    ...range(16, 18, 24), [19, 21], [19, 20],
    [21, 27], [22, 26], [21, 26],
    [4, 34], [5, 34], [6, 34],
    [3, 35], [4, 35], [5, 35],
    [3, 36], [4, 36], [5, 36], [6, 36],
  ])

  const forestCells = cells([
    [7, 17], [8, 17], [9, 17],
    ...range(8, 11, 18),
    ...range(9, 11, 19),
    ...range(2, 4, 20),
    [10, 24], [12, 24],
    [16, 18], [16, 19], [17, 19],
    ...range(20, 22, 24), ...range(20, 22, 25),
    [12, 29], [12, 30], [11, 31], [11, 32], [12, 32], [11, 33], [12, 33],
    [15, 32], [16, 32], [15, 31], [16, 30], [17, 30],
    [32, 32], [33, 32], [34, 32], ...range(32, 34, 33), ...range(31, 34, 34),
    [30, 35], [31, 35], [31, 36],
    ...range(31, 34, 20), ...range(30, 34, 21), ...range(30, 34, 22), ...range(30, 34, 23),
    ...range(32, 34, 24),
  ])

  return [
    { type: 'mountain', cells: mountainCells },
    { type: 'forest', cells: forestCells },
  ]
}
